import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';

const AddPlayerModal = ({ isOpen, onClose, onAdd, refreshPlayerList }) => {
  const [formData, setFormData] = useState({
    fname: '',
    lname: '',
    email: '',
    password: '',
    team: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [teamOptions, setTeamOptions] = useState([]);
  const navigate = useNavigate();

  const fetchData = async (url, options = {}) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.log('No token found, redirecting to sign-in');
      navigate('/auth/sign-in');
      return null;
    }

    const headers = new Headers(options.headers || {});
    headers.set('Authorization', `Token ${token}`);

    try {
      const response = await fetch(url, {
        ...options,
        method: options.method || 'GET',
        headers: headers,
        credentials: 'include',
      });

      if (!response.ok) {
          const errorBody = await response.text();
          console.error('Error response:', errorBody);
        if (response.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          navigate('/auth/sign-in');
          throw new Error('Authentication failed. Please sign in again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching data from ${url}:`, error);
      toast.error(`Failed to fetch data: ${error.message}`);
      return null;
    }
  };

  useEffect(() => {
    const fetchTeams = async () => {
      if (isOpen) {
        const teamsData = await fetchData('https://staging-sit.com/api/teams/');
        if (teamsData) {
          const formattedTeams = teamsData.map(team => ({
            value: team.id,
            label: `${team.team_name} (${team.sport} - ${team.team_category})`
          }));
          setTeamOptions(formattedTeams);
        }
      }
    };

    fetchTeams();
  }, [isOpen, navigate]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTeamChange = (selectedOption) => {
    setFormData({ ...formData, team: selectedOption ? selectedOption.value : null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const addedPlayer = await onAdd(formData);
      console.log('Added player:', addedPlayer);

      if (formData.team) {
        await assignPlayerToTeam(addedPlayer.id, formData.team);
      }

      setFormData({ fname: '', lname: '', email: '', password: '', team: null });
      onClose();
      toast.success("Player added successfully");
      if (typeof refreshPlayerList === 'function') {
        refreshPlayerList();
      }
    } catch (error) {
      console.error('Error adding player or assigning team:', error);
      toast.error(`Failed to add player: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const assignPlayerToTeam = async (playerId, teamId) => {
    return await fetchData(`https://staging-sit.com/api/players/${playerId}/assign_team/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ team_id: teamId })
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-96">
        <h2 className="text-xl font-bold mb-4">Add New Player</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="fname" className="block text-sm font-medium text-gray-700">First Name</label>
            <input
              type="text"
              id="fname"
              name="fname"
              value={formData.fname}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="lname" className="block text-sm font-medium text-gray-700">Last Name</label>
            <input
              type="text"
              id="lname"
              name="lname"
              value={formData.lname}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="team" className="block text-sm font-medium text-gray-700">Team</label>
            <Select
              name="team"
              options={teamOptions}
              className="basic-single"
              classNamePrefix="select"
              onChange={handleTeamChange}
              isClearable={true}
              placeholder="Select team..."
            />
          </div>
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-[#f46524] text-white px-4 py-2 rounded hover:bg-[#f46570] dark:bg-[#7551FF] dark:hover:bg-[#9E8CFC] flex items-center"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Adding...
                </>
              ) : (
                'Add Player'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPlayerModal;